import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import GlassCard from '../ui/GlassCard';
import SectionHeader from '../ui/SectionHeader';
import { strategicPartners } from '../../constants/content';
import { useSectionView } from '../../hooks/useSectionView';

export default function StrategicPartners() {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const sectionRef = useSectionView('strategic_partners', 5);

  return (
    <section
      ref={sectionRef}
      id="partners"
      className={`py-24 ${darkMode ? 'bg-main-black' : 'bg-gray-50'}`}
    >
      <div className="mainCont">
        <SectionHeader
          label={t('partners.label')}
          title={t('partners.headline')}
          highlightColor="purple"
        />

        {/* Description */}
        <p className={`text-center text-base md:text-lg max-w-3xl mx-auto mb-12 ${
          darkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          {t('partners.description')}
        </p>

        {/* Partners Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
          {strategicPartners.map((partner) => (
            <GlassCard
              key={partner.id}
              hover
              padding="md"
              className="text-center group"
            >
              {/* Logo container */}
              <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                partner.id === 'stellar'
                  ? 'bg-white'
                  : darkMode ? 'bg-white/5 group-hover:bg-white/10' : 'bg-gray-100 group-hover:bg-gray-200'
              }`}>
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className={`w-12 h-12 object-contain transition-all duration-300 ${
                    darkMode ? 'opacity-80 group-hover:opacity-100' : 'opacity-70 group-hover:opacity-100'
                  }`}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="text-3xl font-bold ${
                      darkMode ? 'text-main' : 'text-purple-600'
                    }">${partner.name.charAt(0)}</span>`;
                  }}
                />
              </div>

              {/* Name */}
              <h3 className={`titleFont text-lg font-bold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {partner.name}
              </h3>

              {/* Description */}
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {t(`partners.items.${partner.id}.description`)}
              </p>
            </GlassCard>
          ))}
        </div>

        {/* Decorative connection lines (optional visual) */}
        <div className="mt-12 flex justify-center">
          <div className={`flex items-center gap-2 px-6 py-3 rounded-full ${
            darkMode ? 'bg-main/10 border border-main/20' : 'bg-purple-50 border border-purple-100'
          }`}>
            <svg className={`w-5 h-5 ${darkMode ? 'text-main' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span className={`text-sm font-medium ${darkMode ? 'text-main' : 'text-purple-700'}`}>
              {t('partners.strongerTogether')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
